/**
 * ChangePasswordSheet — modal sheet invoked from Settings.
 * Supports two modes:
 *   1) Email user with existing password (requires current password)
 *   2) Google user without a password (initial set, no current password)
 */
import { useState } from "react";
import { X, Eye, EyeOff, Check } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function ChangePasswordSheet({ hasPassword = true, onClose, onSuccess }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const disabled =
    busy || next.length < 8 || next !== confirm || (hasPassword && cur.length === 0);

  const submit = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: hasPassword ? cur : null,
        new_password: next,
      });
      toast.success(hasPassword ? "Password updated" : "Password set — you can now log in with email");
      onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40"
      data-testid="change-password-sheet"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-[24px] rounded-t-[24px] p-6 pb-8"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-[22px]">
            {hasPassword ? "Change Password" : "Set a password"}
          </h3>
          <button
            onClick={onClose}
            data-testid="cp-close"
            aria-label="Close"
            className="w-9 h-9 -mr-2 grid place-items-center rounded-full hover:bg-canvas2"
          >
            <X size={18} />
          </button>
        </div>
        {!hasPassword && (
          <p className="text-[12.5px] text-ink-soft -mt-0.5 mb-3">
            You signed up with Google. Set a password to also sign in with email.
          </p>
        )}

        <div className="space-y-3 mt-3">
          {hasPassword && (
            <PasswordField
              label="Current password"
              value={cur}
              onChange={setCur}
              visible={showCur}
              onToggle={() => setShowCur((s) => !s)}
              testid="cp-current"
            />
          )}
          <PasswordField
            label="New password (min 8 characters)"
            value={next}
            onChange={setNext}
            visible={showNext}
            onToggle={() => setShowNext((s) => !s)}
            testid="cp-new"
            error={tooShort ? "Use at least 8 characters" : null}
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            visible={showNext}
            onToggle={() => setShowNext((s) => !s)}
            testid="cp-confirm"
            error={mismatch ? "Passwords do not match" : null}
          />
        </div>

        <button
          onClick={submit}
          disabled={disabled}
          data-testid="cp-submit"
          className="mt-5 w-full h-12 rounded-pill disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
        >
          {busy ? "Saving…" : (
            <>
              <Check size={16} /> {hasPassword ? "Update password" : "Set password"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, visible, onToggle, testid, error }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-1.5">
        {label}
      </div>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full h-12 px-3 pr-11 rounded-[12px] border border-line bg-canvas2 outline-none text-[14px]"
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={visible ? "Hide" : "Show"}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-ink-soft"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error && <div className="text-[11.5px] text-red-500 mt-1">{error}</div>}
    </div>
  );
}
